import { getType } from '@reduxjs/toolkit';
import { call, put, takeLatest } from 'redux-saga/effects';

import { IHealthcareApi, IOrganization, IOrganizationDevices, IOrganizationUsers } from '../../interfaces';
import { convertToError } from '../../utils';
import * as actions from './actions';

function* fetchOrganization(api: IHealthcareApi) {
  try {
    const organization: IOrganization = yield call(api.fetchOrganization);

    yield put(actions.fetchOrganizationSucceeded({ organization }));
  } catch (e) {
    yield put(actions.fetchOrganizationFailed(convertToError(e)));
  }
}

function* fetchOrganizationDevices(api: IHealthcareApi) {
  try {
    const devices: IOrganizationDevices = yield call(api.fetchOrganizationDevices);

    yield put(actions.fetchOrganizationDevicesSucceeded({ devices }));
  } catch (e) {
    yield put(actions.fetchOrganizationDevicesFailed(convertToError(e)));
  }
}

function* removeOrganizationDevice(api: IHealthcareApi, action: ReturnType<typeof actions.removeOrganizationDevice>) {
  const { device } = action.payload;

  try {
    yield call(api.removeOrganizationDevice, device);
    const devices: IOrganizationDevices = yield call(api.fetchOrganizationDevices);

    yield put(actions.removeOrganizationDeviceSucceeded({ devices }));
  } catch (e) {
    yield put(actions.removeOrganizationDeviceFailed(convertToError(e)));
  }
}

function* registerOrganizationDevices(
  api: IHealthcareApi,
  action: ReturnType<typeof actions.registerOrganizationDevices>,
) {
  const { request } = action.payload;

  try {
    yield call(api.registerOrganizationDevices, request);

    yield put(actions.registerOrganizationDevicesSucceeded());
  } catch (e) {
    yield put(actions.registerOrganizationDevicesFailed(convertToError(e)));
  }
}

function* orderOrganizationDevices(api: IHealthcareApi, action: ReturnType<typeof actions.orderOrganizationDevices>) {
  const { request } = action.payload;

  try {
    yield call(api.orderOrganizationDevices, request);

    yield put(actions.orderOrganizationDevicesSucceeded());
  } catch (e) {
    yield put(actions.orderOrganizationDevicesFailed(convertToError(e)));
  }
}

function* fetchOrganizationUsers(api: IHealthcareApi) {
  try {
    const users: IOrganizationUsers = yield call(api.fetchOrganizationUsers);

    yield put(actions.fetchOrganizationUsersSucceeded({ users }));
  } catch (e) {
    yield put(actions.fetchOrganizationUsersFailed(convertToError(e)));
  }
}

function* createOrganizationUser(api: IHealthcareApi, action: ReturnType<typeof actions.createOrganizationUser>) {
  const { user } = action.payload;

  try {
    yield call(api.createOrganizationUser, user);
    const users: IOrganizationUsers = yield call(api.fetchOrganizationUsers);

    yield put(actions.createOrganizationUserSucceeded({ users }));
  } catch (e) {
    yield put(actions.createOrganizationUserFailed(convertToError(e)));
  }
}

function* changeOrganizationUserRole(
  api: IHealthcareApi,
  action: ReturnType<typeof actions.changeOrganizationUserRole>,
) {
  const { user } = action.payload;

  try {
    yield call(api.changeOrganizationUserRole, user);
    const users: IOrganizationUsers = yield call(api.fetchOrganizationUsers);

    yield put(actions.changeOrganizationUserRoleSucceeded({ users }));
  } catch (e) {
    yield put(actions.changeOrganizationUserRoleFailed(convertToError(e)));
  }
}

function* deleteOrganizationUser(api: IHealthcareApi, action: ReturnType<typeof actions.deleteOrganizationUser>) {
  const { user } = action.payload;

  try {
    yield call(api.deleteOrganizationUser, user);
    const users: IOrganizationUsers = yield call(api.fetchOrganizationUsers);

    yield put(actions.deleteOrganizationUserSucceeded({ users }));
  } catch (e) {
    yield put(actions.deleteOrganizationUserFailed(convertToError(e)));
  }
}

export function* organizationSaga(api: IHealthcareApi) {
  yield takeLatest(getType(actions.fetchOrganization), fetchOrganization, api);
  yield takeLatest(getType(actions.fetchOrganizationDevices), fetchOrganizationDevices, api);
  yield takeLatest(getType(actions.removeOrganizationDevice), removeOrganizationDevice, api);
  yield takeLatest(getType(actions.registerOrganizationDevices), registerOrganizationDevices, api);
  yield takeLatest(getType(actions.orderOrganizationDevices), orderOrganizationDevices, api);
  yield takeLatest(getType(actions.fetchOrganizationUsers), fetchOrganizationUsers, api);
  yield takeLatest(getType(actions.createOrganizationUser), createOrganizationUser, api);
  yield takeLatest(getType(actions.changeOrganizationUserRole), changeOrganizationUserRole, api);
  yield takeLatest(getType(actions.deleteOrganizationUser), deleteOrganizationUser, api);
}
